import React, { useEffect, useState } from 'react';
import useGenerateInvestmentMemo, { GenerateInvestmentMemoResponse } from 'queries/OpenAI/useGenerateInvestmentMemo';
import { useDebounce } from 'hooks/useDebounce';
import { InvestmentMemoFormData, SelectedProFormaOption, GPTCheckboxItem } from '../types';

export interface AISuggestions {
    highlights: GPTCheckboxItem[];
    drivers: GPTCheckboxItem[];
    fundamentals: GPTCheckboxItem[];
}

interface UseGenerateInvestmentMemoContentProps {
    selectedProFormaOption: SelectedProFormaOption | undefined;
    organizationId: string;
    isTitleAddressLoaded: boolean;
    addressFields: {
        address: string;
        city: string;
        state: string;
        zipCode: string;
    };
    setFormData: React.Dispatch<React.SetStateAction<InvestmentMemoFormData>>;
}

export const useGenerateInvestmentMemoContent = ({
    selectedProFormaOption,
    organizationId,
    isTitleAddressLoaded,
    addressFields,
    setFormData,
}: UseGenerateInvestmentMemoContentProps) => {
    const { mutate: generateContent, isPending: isGenerating } = useGenerateInvestmentMemo();
    const [aiSuggestions, setAiSuggestions] = useState<AISuggestions>({
        highlights: [],
        drivers: [],
        fundamentals: [],
    });

    const debouncedAddressFields = useDebounce(addressFields, 2000);

    useEffect(() => {
        if (!selectedProFormaOption || !isTitleAddressLoaded) {
            return;
        }

        generateContent(
            {
                organizationId,
                dealId: selectedProFormaOption.value.dealId,
                proFormaId: selectedProFormaOption.value.id,
                addressFields: debouncedAddressFields,
            },
            {
                onSuccess: (response: GenerateInvestmentMemoResponse) => {
                    console.log('AI Response:', response);

                    const aiHighlights = response.investmentHighlights.map(item => ({
                        id: crypto.randomUUID(),
                        name: item.header,
                        description: item.description,
                        isChecked: true,
                        isGenerated: true,
                    }));

                    const aiDrivers = response.locationProximityDrivers.map(item => ({
                        id: crypto.randomUUID(),
                        name: item.header,
                        description: item.description,
                        isChecked: true,
                        isGenerated: true,
                    }));

                    const aiFundamentals = response.marketOverviewFundamentals.map(item => ({
                        id: crypto.randomUUID(),
                        name: item.header,
                        description: item.description,
                        isChecked: true,
                        isGenerated: true,
                    }));

                    setAiSuggestions({
                        highlights: aiHighlights,
                        drivers: aiDrivers,
                        fundamentals: aiFundamentals,
                    });

                    setFormData(prev => ({
                        ...prev,
                        title: {
                            ...prev.title,
                            tagline: response.tagline,
                        },
                        executiveSummary: {
                            ...prev.executiveSummary,
                            opportunityDescription: response.opportunity,
                        },
                        location: {
                            ...prev.location,
                            drivers: aiDrivers.filter(d => d.isChecked),
                        },
                    }));
                },
                onError: error => {
                    console.error('Error generating content:', error);
                },
            },
        );
    }, [selectedProFormaOption, organizationId, debouncedAddressFields, isTitleAddressLoaded, generateContent, setFormData, setAiSuggestions]);

    return { isGenerating, aiSuggestions };
};
